import type { Household } from 'src/types/household';
import { useMemo, useState } from 'react';
import router from 'next/router';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import SearchableCombobox from '@savvy-kit/patterns/searchable-combobox';
import { useDebounce } from 'src/hooks/use-debounce';

import { useHouseholdSearch } from '../../../utils/household/useHouseholdSearch';
import { HouseholdSearchResult } from './household-search-result';

export function GlobalHouseholdSearchBar() {
  const [searchText, setSearchText] = useState('');
  const query = useDebounce(searchText, 250).trim();
  const { data, loading } = useHouseholdSearch({ query });

  const households = useMemo(() => data?.households.edges.map((edge) => edge.node) ?? [], [data]);
  const options = useMemo(
    () => households.map((household) => ({ value: household.id, label: household.name })),
    [households],
  );

  return (
    <div className="flex max-w-[365px] flex-auto flex-row items-center">
      <SearchableCombobox
        className="w-full"
        label="Search for a household"
        placeholder="Search for a household"
        options={options}
        value=""
        shouldFilter={false}
        showCaret={false}
        isLoading={loading && query !== ''}
        emptyMessage={query === '' ? 'Type to search households.' : 'No results found.'}
        onSearchChange={setSearchText}
        onValueChange={(id) => {
          const household = households.find((h) => h.id === id);
          if (household) {
            router.push(`/advisor/dashboard/client/${household.salesforceAccountId}`);
            setSearchText('');
          }
        }}
        renderOption={(option) => {
          const household = households.find((h) => h.id === option.value);
          return household ? <HouseholdSearchResult household={household} /> : <Text>{option.label}</Text>;
        }}
      />
    </div>
  );
}

export function HouseholdMenuItem({
  household,
  index,
  onClick,
  width,
}: {
  readonly household: Household;
  readonly index: number;
  readonly onClick: () => void;
  readonly width?: string;
}) {
  return (
    <div
      key={household.id}
      onClick={onClick}
      style={width ? { width } : undefined}
      className={cn(
        'flex max-w-full cursor-pointer items-center px-2 py-4 hover:bg-[rgba(63,143,224,0.08)]',
        index === 0 ? '' : 'border-t border-border-light',
      )}
    >
      <HouseholdSearchResult household={household} />
    </div>
  );
}
